/**
 * Utility functions for filtering dashboard data based on user settings
 */

/**
 * Check if a category should be excluded
 * @param {string} categoryName - The category name
 * @param {Array} excludedCategories - Array of excluded category names/IDs
 * @returns {boolean}
 */
export const isCategoryExcluded = (categoryName, excludedCategories) => {
  if (!excludedCategories || excludedCategories.length === 0) {
    return false
  }
  return excludedCategories.includes(categoryName)
}

/**
 * Check if a subcategory should be excluded
 * @param {number} subcategoryId - The subcategory ID
 * @param {Array} excludedSubcategories - Array of excluded subcategory IDs
 * @returns {boolean}
 */
export const isSubcategoryExcluded = (subcategoryId, excludedSubcategories) => {
  if (!excludedSubcategories || excludedSubcategories.length === 0) {
    return false
  }
  return excludedSubcategories.includes(subcategoryId)
}

/**
 * Filter operations based on dashboard settings
 * @param {Array} operations - Array of operations
 * @param {Object} settings - Dashboard settings object
 * @returns {Array} - Filtered operations
 */
export const filterOperations = (operations, settings) => {
  if (!operations || operations.length === 0) {
    return []
  }

  const { excluded_categories = [], excluded_subcategories = [] } = settings

  return operations.filter((operation) => {
    // Check if operation has category/subcategory info
    if (operation.SousCategorie) {
      // Handle array of subcategories
      if (Array.isArray(operation.SousCategorie)) {
        return !operation.SousCategorie.some((subcat) => {
          const categoryExcluded = isCategoryExcluded(subcat.Categorie, excluded_categories)
          const subcategoryExcluded = isSubcategoryExcluded(subcat.id, excluded_subcategories)
          return categoryExcluded || subcategoryExcluded
        })
      }
      // Handle single subcategory object
      const categoryExcluded = isCategoryExcluded(
        operation.SousCategorie.Categorie,
        excluded_categories
      )
      const subcategoryExcluded = isSubcategoryExcluded(
        operation.SousCategorie.id,
        excluded_subcategories
      )
      return !categoryExcluded && !subcategoryExcluded
    }
    return true
  })
}

/**
 * Filter chart data based on dashboard settings
 * @param {Array} chartData - Chart data with categories
 * @param {Object} settings - Dashboard settings object
 * @returns {Array} - Filtered chart data
 */
export const filterChartData = (chartData, settings) => {
  if (!chartData || chartData.length === 0) {
    return []
  }

  const { excluded_categories = [], excluded_subcategories = [] } = settings

  return chartData.filter((item) => {
    // Filter by category name if present
    if (item.name || item.label) {
      const name = item.name || item.label
      return !excluded_categories.includes(name)
    }
    // Filter by subcategory ID if present
    if (item.id) {
      return !excluded_subcategories.includes(item.id)
    }
    return true
  })
}

/**
 * Filter aggregated totals based on dashboard settings
 * @param {Object} totals - Object with total calculations
 * @param {Array} operations - Raw operations data
 * @param {Object} settings - Dashboard settings object
 * @returns {Object} - Recalculated totals
 */
export const recalculateTotals = (operations, settings) => {
  const filteredOps = filterOperations(operations, settings)

  const totals = {
    income: 0,
    expenses: 0,
    cashflow: 0,
    assets: 0,
    liabilities: 0,
  }

  filteredOps.forEach((op) => {
    const amount = parseFloat(op.Montant) || 0
    if (amount > 0) {
      totals.income += amount
    } else {
      totals.expenses += Math.abs(amount)
    }
  })

  totals.cashflow = totals.income - totals.expenses

  return totals
}

/**
 * Apply dashboard settings filters to report data
 * @param {Array} reports - Array of report cards
 * @param {Object} settings - Dashboard settings object
 * @returns {Array} - Filtered reports
 */
export const filterReports = (reports, settings) => {
  if (!reports || reports.length === 0 || !settings) {
    return reports
  }

  return reports.map((report) => {
    // If report has calculation data, filter it
    if (report.calculation && report.calculation.group_of_categories) {
      return {
        ...report,
        calculation: {
          ...report.calculation,
          group_of_categories: filterChartData(
            report.calculation.group_of_categories,
            settings
          ),
        },
      }
    }
    return report
  })
}
